import React, { useState, useEffect, useCallback } from 'react';
import { AiTwotoneSetting } from "react-icons/ai";
import { FaEnvelope, FaServer, FaFlask, FaTrash, FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';
import { emailConfigurationAPI } from '../../api/emailConfigurationService';
import UserPcode from '../../components/userPcode';
import Spinner from '../../components/spinner';
import { toast } from 'react-toastify';
import useUserName from '../../common/hooks/useUserName';
import './EmailConfiguration.css';
import usePcode from '../../common/hooks/usePcode';

const EmailConfiguration = () => {
    const { userName } = useUserName();
    const { loggedPcode } = usePcode();

    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedPcode, setSelectedPcode] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);

    // Email configuration state
    const [config, setConfig] = useState({
        pCode: '',
        smtpServer: '',
        smtpPort: 587,
        enableSSL: true,
        senderEmail: '',
        senderName: '',
        plainPassword: '',
        emailProvider: 'Gmail',
        customProviderName: '',
        isActive: true,
        createdBy: 'Admin',
        modifiedBy: 'Admin',
        remarks: ''
    });

    // Test email state
    const [testEmail, setTestEmail] = useState({
        recipientEmail: '',
        recipientName: '',
        testMessage: 'This is a test email to verify your email configuration is working correctly.'
    });

    const [currentConfig, setCurrentConfig] = useState(null);

    const loadEmailConfiguration = useCallback(async (pCode) => {
        setLoading(true);
        try {
            const response = await emailConfigurationAPI.getEmailConfiguration(pCode);

            if (response.success) {
                setCurrentConfig(response.data);
                setConfig({
                    ...response.data,
                    plainPassword: '', // Don't populate password field for security
                    modifiedBy: 'Admin'
                });
                toast.success('Email configuration loaded successfully');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                toast.warn('No email configuration found for this company. You can create a new one.');
            } else {
                toast.error('Error loading email configuration: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (loggedPcode) {
            setConfig(prev => ({ ...prev, pCode: loggedPcode }));
            setSelectedPcode(loggedPcode);
            loadEmailConfiguration(loggedPcode);
        }
    }, [loadEmailConfiguration]);

    const onChangePCode = (pcode) => {
        setSelectedPcode(pcode);
        setConfig(prev => ({ ...prev, pCode: pcode }));
        if (pcode) {
            loadEmailConfiguration(pcode);
        }
    };

    const handleProviderChange = (provider) => {
        const templates = emailConfigurationAPI.getEmailProviderTemplates();
        const template = templates[provider];

        setConfig(prev => ({
            ...prev,
            emailProvider: provider,
            smtpServer: template.smtpServer,
            smtpPort: template.smtpPort,
            enableSSL: template.enableSSL
        }));
    };

    const handleInputChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveConfiguration = async () => {
        // Validate configuration
        const validation = emailConfigurationAPI.validateEmailConfiguration(config);
        if (!validation.isValid) {
            validation.errors.forEach(error => toast.error(error));
            return;
        }

        setSaving(true);
        try {
            const response = await emailConfigurationAPI.saveEmailConfiguration(config);
            if (response.success) {
                toast.success('Email configuration saved successfully!');
                setCurrentConfig(config);
                // Clear password field after saving
                setConfig(prev => ({ ...prev, plainPassword: '' }));
            } else {
                toast.error('Failed to save email configuration');
            }
        } catch (error) {
            toast.error('Error saving configuration: ' + (error.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail.recipientEmail) {
            toast.error('Please enter a recipient email address');
            return;
        }

        setTesting(true);
        try {
            const response = await emailConfigurationAPI.testEmailConfiguration(config.pCode, testEmail);
            if (response.success) {
                toast.success('Test email sent successfully!');
                setTestModalOpen(false);
            } else {
                toast.error('Failed to send test email: ' + response.message);
            }
        } catch (error) {
            toast.error('Error sending test email: ' + (error.response?.data?.message || error.message));
        } finally {
            setTesting(false);
        }
    };



    const getProviderInstructions = () => {
        const templates = emailConfigurationAPI.getEmailProviderTemplates();
        return templates[config.emailProvider]?.instructions || '';
    };

    return (
        <div className="Bg">
            <div className="email-config-wrapper">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-8 col-xl-8">
                            <div className="contentEmailConfig rounded">

                                {/* Header */}
                                <div className="row">
                                    <div className="col-12 text-center">
                                        <h4>Email Configuration</h4>
                                    </div>
                                </div>

                                {/* Company Selection */}
                                <div className='row mt-2'>
                                    <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                        <label className='mt-1'> Center : </label>
                                    </div>
                                    <div className='col-12 col-md-10'>
                                        <div className="input-group input-group-sm">
                                            <UserPcode
                                                setSelectedPcode={setSelectedPcode}
                                                triggerOnChangePCode={onChangePCode} />
                                        </div>
                                    </div>
                                </div>

                                {/* Loading Spinner */}
                                {loading && (
                                    <div className="row">
                                        <div className="col-12 text-center">
                                            <Spinner />
                                            <p className="mt-2">Loading email configuration...</p>
                                        </div>
                                    </div>
                                )}

                                {/* Configuration Form */}
                                {!loading && selectedPcode && (
                                    <>
                                        {/* Status Badge */}
                                        {currentConfig && (
                                            <div className="row mt-2">
                                                <div className="col-12 text-center">
                                                    <span className="badge bg-success">Active Configuration Found</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Provider Selection */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Provider : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="input-group input-group-sm">
                                                    <select
                                                        className="form-select"
                                                        value={config.emailProvider}
                                                        onChange={(e) => handleProviderChange(e.target.value)}
                                                    >
                                                        <option value="Gmail">Gmail</option>
                                                        <option value="Outlook">Outlook</option>
                                                        <option value="Office365">Office 365</option>
                                                        <option value="Custom">Custom SMTP</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Custom Provider Name */}
                                        {config.emailProvider === 'Other' && (
                                            <div className="row mt-2">
                                                <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                    <label className='mt-1'> Provider Name : </label>
                                                </div>
                                                <div className='col-12 col-md-10'>
                                                    <div className="input-group input-group-sm">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={config.customProviderName}
                                                            onChange={(e) => handleInputChange('customProviderName', e.target.value)}
                                                            placeholder="Enter provider name"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Provider Instructions */}
                                        {getProviderInstructions() && (
                                            <div className="row mt-2">
                                                <div className="col-12">
                                                    <div className="alert alert-info">
                                                        <strong>Instructions:</strong> {getProviderInstructions()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* SMTP Server and Port */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> SMTP Server : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="row g-2">
                                                    <div className="col-12 col-sm-8">
                                                        <div className="input-group input-group-sm">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={config.smtpServer}
                                                                onChange={(e) => handleInputChange('smtpServer', e.target.value)}
                                                                placeholder="smtp.gmail.com"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-sm-4">
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text d-sm-none">Port:</span>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={config.smtpPort}
                                                                onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                                                                min="1"
                                                                max="65535"
                                                                placeholder="Port (e.g., 587)"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sender Email */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Email : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={config.senderEmail}
                                                        onChange={(e) => handleInputChange('senderEmail', e.target.value)}
                                                        placeholder="notifications@company.com"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sender Name */}
                                        <div className='row mt-2'>
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Name : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={config.senderName}
                                                        onChange={(e) => handleInputChange('senderName', e.target.value)}
                                                        placeholder="Leave System"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Password */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Password : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control"
                                                        value={config.plainPassword}
                                                        onChange={(e) => handleInputChange('plainPassword', e.target.value)}
                                                        placeholder={currentConfig ? "Enter new password to update" : "Enter password"}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Checkboxes */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Options : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-3">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={config.enableSSL}
                                                            onChange={(e) => handleInputChange('enableSSL', e.target.checked)}
                                                        />
                                                        <label className="form-check-label">Enable SSL/TLS</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={config.isActive}
                                                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                                        />
                                                        <label className="form-check-label">Active Configuration</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Remarks */}
                                        <div className="row mt-2">
                                            <div className='col-12 col-md-2 mb-2 mb-md-0'>
                                                <label className='mt-1'> Remarks : </label>
                                            </div>
                                            <div className='col-12 col-md-10'>
                                                <div className="input-group input-group-sm">
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        value={config.remarks}
                                                        onChange={(e) => handleInputChange('remarks', e.target.value)}
                                                        placeholder="Additional notes about this configuration"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="row mt-3">
                                            <div className="col-12 text-center">
                                                <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                                                    <button
                                                        onClick={handleSaveConfiguration}
                                                        disabled={saving}
                                                        type="button"
                                                        className="btn btn-success btn-sm"
                                                    >
                                                        {saving ? <Spinner /> : <FaSave />}
                                                        {saving ? ' Saving...' : ' Save Configuration'}
                                                    </button>

                                                    {/*{currentConfig && (*/}
                                                    {/*    <button*/}
                                                    {/*        onClick={() => setTestModalOpen(true)}*/}
                                                    {/*        type="button"*/}
                                                    {/*        className="btn btn-info btn-sm"*/}
                                                    {/*    >*/}
                                                    {/*        <FaFlask /> Test Email*/}
                                                    {/*    </button>*/}
                                                    {/*)}*/}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Test Email Modal */}
            {testModalOpen && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FaFlask className="me-2" />
                                    Send Test Email
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setTestModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Recipient Email *</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={testEmail.recipientEmail}
                                        onChange={(e) => setTestEmail({ ...testEmail, recipientEmail: e.target.value })}
                                        placeholder="test@example.com"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Recipient Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={testEmail.recipientName}
                                        onChange={(e) => setTestEmail({ ...testEmail, recipientName: e.target.value })}
                                        placeholder="Test User"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Test Message</label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={testEmail.testMessage}
                                        onChange={(e) => setTestEmail({ ...testEmail, testMessage: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleTestEmail}
                                    disabled={testing}
                                >
                                    {testing ? <Spinner /> : <FaEnvelope />}
                                    {testing ? ' Sending...' : ' Send Test Email'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setTestModalOpen(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailConfiguration;
